import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";

const SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let loader = null;
const site_key =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "1x00000000000000000000AA";

function loadScript() {
  if (window.turnstile) return Promise.resolve();

  if (!loader) {
    loader = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  }

  return loader;
}

const Turnstile = forwardRef(({ onVerify }, ref) => {
  const containerRef = useRef(null);
  const widgetRenderedRef = useRef(false);
  const onVerifyRef = useRef(onVerify);

  const [ready, setReady] = useState(false);

  // ✅ keep latest callback reference
  useEffect(() => {
    onVerifyRef.current = onVerify;
  }, [onVerify]);

  // ✅ load script once
  useEffect(() => {
    loadScript().then(() => setReady(true));
  }, []);

  // ✅ render widget (ONLY ONCE)
  useEffect(() => {
    if (!ready || !containerRef.current || widgetRenderedRef.current) return;

    window.turnstile.render(containerRef.current, {
      sitekey: site_key, // ✅ use real key in prod
      execution: "execute",
      appearance: "interaction-only",
      callback: (token) => {
        onVerifyRef.current?.(token);
      },

      "error-callback": () => {},

      "expired-callback": () => {},
    });

    widgetRenderedRef.current = true;
  }, [ready]);

  // ✅ expose methods
  useImperativeHandle(ref, () => ({
    execute: () => {
      if (!containerRef.current) return;

      window.turnstile.execute(containerRef.current, {
        sitekey: site_key,
      });
    },

    reset: () => {
      if (!containerRef.current) return;

      window.turnstile.reset(containerRef.current);
    },
  }));

  return <div ref={containerRef} />;
});

export default Turnstile;
